import { Button, ButtonProps } from "../Button";
import { useCalendar } from "./CalendarContext";
import { MouseEvent, forwardRef, useEffect, useState } from "react";
import Next from "@servicetitan/hammer-icon/mdi/round/keyboard_arrow_right.svg";
import { isSameMonth } from "./internal/utils";
import cx from "classnames";
import styles from "./Calendar.module.scss";

export type CalendarNextProps = ButtonProps;

/**
 * A button that displays the next month and allows the user to select a different month.
 *
 * @param props.onClick - The function to call when the button is clicked.
 * @param props.className - The class name to apply to the button.
 * @param props - The rest of the props for the Button component.
 * @returns A button that displays the next month and allows the user to select a different month.
 */
export const CalendarNext = forwardRef<HTMLButtonElement, CalendarNextProps>(
  (props, ref) => {
    const { onClick, className, ...rest } = props;
    const {
      focusedDate,
      setFocusedDate,
      maxDate,
      setSelectedMonth,
      setSelectedYear,
    } = useCalendar();

    const [disabled, setDisabled] = useState(false);

    useEffect(() => {
      setDisabled(
        maxDate != null &&
          focusedDate.plus({ month: 1 }).startOf("month") > maxDate,
      );
    }, [maxDate, focusedDate]);

    const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);

      const targetMonth = focusedDate.plus({ month: 1 }).startOf("month");
      const targetDate = focusedDate.plus({ month: 1 });

      // Check if we're crossing year boundary (December to January)
      const isCrossingYearBoundary =
        focusedDate.month === 12 && targetDate.month === 1;

      if (maxDate !== null) {
        if (targetDate.toMillis() <= maxDate?.toMillis()) {
          setFocusedDate(targetDate);
          setSelectedMonth(targetDate.month);
          if (isCrossingYearBoundary) {
            setSelectedYear(targetDate.year);
          }
          return;
        }

        if (!isSameMonth(targetMonth, focusedDate)) {
          setFocusedDate(maxDate);
          setSelectedMonth(maxDate.month);
          if (isCrossingYearBoundary) {
            setSelectedYear(maxDate.year);
          }
        }
        return;
      }
      setFocusedDate(targetDate);
      setSelectedMonth(targetDate.month);
      if (isCrossingYearBoundary) {
        setSelectedYear(targetDate.year);
      }
    };

    return (
      <Button
        onClick={onClickHandler}
        appearance="ghost"
        size="small"
        className={cx(styles["controller-button"], className)}
        icon={Next}
        aria-label="Next Month"
        data-anv="calendar-next"
        ref={ref}
        {...rest}
        disabled={disabled}
      />
    );
  },
);

CalendarNext.displayName = "CalendarNextButton";
